import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, DollarSign, RefreshCw, X
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import Loading from '../../components/Loading';

const AdminExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [categoryFilter, setCategoryFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    vendor: '',
    description: '',
    amount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    isDeductible: true,
    deductiblePercent: 100,
    referenceNumber: '',
    notes: '',
    isRecurring: false,
    recurrencePattern: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
    loadExpenses();
  }, [yearFilter, categoryFilter]);

  const loadCategories = async () => {
    try {
      const response = await adminApi.getExpenseCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getExpenses({
        year: yearFilter,
        category: categoryFilter || undefined
      });
      setExpenses(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: categories[0]?.value || '',
      vendor: '',
      description: '',
      amount: '',
      expenseDate: new Date().toISOString().split('T')[0],
      isDeductible: true,
      deductiblePercent: 100,
      referenceNumber: '',
      notes: '',
      isRecurring: false,
      recurrencePattern: ''
    });
    setEditingExpense(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      vendor: expense.vendor,
      description: expense.description,
      amount: expense.amount.toString(),
      expenseDate: expense.expenseDate.split('T')[0],
      isDeductible: expense.isDeductible,
      deductiblePercent: expense.deductiblePercent,
      referenceNumber: expense.referenceNumber || '',
      notes: expense.notes || '',
      isRecurring: expense.isRecurring,
      recurrencePattern: expense.recurrencePattern || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        deductiblePercent: parseFloat(formData.deductiblePercent)
      };

      if (editingExpense) {
        await adminApi.updateExpense(editingExpense.id, payload);
      } else {
        await adminApi.createExpense(payload);
      }

      setShowModal(false);
      resetForm();
      loadExpenses();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expense) => {
    if (!confirm(`Delete: ${expense.vendor} - ${expense.description}?`)) return;

    try {
      await adminApi.deleteExpense(expense.id);
      loadExpenses();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete expense');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalDeductible = expenses.reduce((sum, e) => 
    sum + (e.isDeductible ? e.amount * (e.deductiblePercent / 100) : 0), 0);

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Platform Expenses</h1>
          <p className="text-sm text-gray-600">Track SuddyFlow costs for Schedule C</p>
        </div>
        <button onClick={openCreateModal} className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto">
          <Plus size={16} />
          Add Expense
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="card p-3 sm:p-4">
          <p className="text-xs text-gray-500 font-medium">Total Expenses</p>
          <p className="text-lg sm:text-2xl font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
          <p className="text-xs text-gray-400">{yearFilter}</p>
        </div>
        <div className="card p-3 sm:p-4">
          <p className="text-xs text-gray-500 font-medium">Deductible</p>
          <p className="text-lg sm:text-2xl font-bold text-green-600">{formatCurrency(totalDeductible)}</p>
          <p className="text-xs text-gray-400">Tax deductions</p>
        </div>
        <div className="card p-3 sm:p-4">
          <p className="text-xs text-gray-500 font-medium">Count</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{expenses.length}</p>
          <p className="text-xs text-gray-400">Transactions</p>
        </div>
        <div className="card p-3 sm:p-4">
          <p className="text-xs text-gray-500 font-medium">Avg/Month</p>
          <p className="text-lg sm:text-2xl font-bold text-blue-600">{formatCurrency(totalExpenses / 12)}</p>
          <p className="text-xs text-gray-400">Run rate</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3 sm:p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Year</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">All Categories</option>
              {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={loadExpenses} className="btn btn-secondary flex items-center gap-2 text-sm">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6 text-sm">{error}</div>
      )}

      {/* Expenses List */}
      {loading ? (
        <Loading />
      ) : expenses.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">
          <DollarSign size={40} className="mx-auto mb-4 text-gray-300" />
          <p className="text-base font-medium mb-2">No expenses recorded</p>
          <p className="text-sm mb-4">Start tracking costs for tax deductions.</p>
          <button onClick={openCreateModal} className="btn btn-primary inline-flex items-center gap-2 text-sm">
            <Plus size={16} />
            Add First Expense
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="card overflow-hidden hidden lg:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Sch C</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">{formatDate(expense.expenseDate)}</td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{expense.vendor}</span>
                        {expense.isRecurring && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded">
                            {expense.recurrencePattern}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{expense.description}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                          {expense.categoryDisplayName}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 text-right">
                        {formatCurrency(expense.amount)}
                        {expense.deductiblePercent < 100 && (
                          <span className="text-xs text-gray-400 block">({expense.deductiblePercent}%)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-gray-500">L{expense.scheduleCLine}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEditModal(expense)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(expense)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr>
                    <td colSpan={4} className="px-4 py-3 font-medium text-gray-700">Total</td>
                    <td className="px-4 py-3 font-bold text-gray-900 text-right">{formatCurrency(totalExpenses)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Mobile Card Layout */}
          <div className="lg:hidden space-y-3">
            {expenses.map((expense) => (
              <div key={expense.id} className="card p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-sm truncate">{expense.vendor}</p>
                    <p className="text-xs text-gray-500 truncate">{expense.description}</p>
                  </div>
                  <p className="font-bold text-gray-900 text-sm flex-shrink-0">{formatCurrency(expense.amount)}</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{formatDate(expense.expenseDate)}</span>
                    <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">{expense.categoryDisplayName}</span>
                    {expense.isRecurring && (
                      <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">{expense.recurrencePattern}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditModal(expense)} className="p-1.5 text-gray-400 hover:text-blue-600">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(expense)} className="p-1.5 text-gray-400 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Mobile Total */}
            <div className="card p-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">Total</span>
                <span className="font-bold text-gray-900">{formatCurrency(totalExpenses)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingExpense ? 'Edit Expense' : 'Add Expense'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  >
                    <option value="">Select</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label} (L{cat.scheduleCLine})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.expenseDate}
                    onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                <input
                  type="text"
                  value={formData.vendor}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  required
                  placeholder="e.g., Microsoft Azure"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="e.g., Monthly hosting"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      placeholder="0.00"
                      className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deductible %</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.deductiblePercent}
                    onChange={(e) => setFormData({ ...formData, deductiblePercent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.isRecurring}
                    onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                    className="h-4 w-4 text-primary-600 rounded"
                  />
                  Recurring
                </label>
                {formData.isRecurring && (
                  <select
                    value={formData.recurrencePattern}
                    onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value })}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="">Pattern</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annual">Annual</option>
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional details..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
            </form>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 flex-shrink-0">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
              >
                {saving ? 'Saving...' : editingExpense ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExpenses;